import { IconButton } from "@mui/material";
import { IoClose } from "react-icons/io5";
import { ReactComponent as PenIcon } from "../../../../../assets/svg/pen.svg";
import { ReactComponent as FeltTipPenIcon } from "../../../../../assets/svg/felt-tip-pen.svg";
import { ReactComponent as EraserIcon } from "../../../../../assets/svg/eraser.svg";
import { ReactComponent as BrushIcon } from "../../../../../assets/svg/brush.svg";
import React, { useRef, useState } from "react";

interface ToolbarProps {
  selectedToolId: string | null;
  setSelectedToolId: (toolId: string | null) => void;
  selectedColor: string | null;
  setSelectedColor: (color: string | null) => void;
  onClear: () => void;
  onHide: () => void;
  style?: React.CSSProperties;
}

const tools = [
  { id: "pen", Icon: PenIcon },
  { id: "highlighter", Icon: FeltTipPenIcon },
  { id: "eraser", Icon: EraserIcon },
];

// const colors = [
//   "#000000",
//   "#404040",
//   "#808080",
//   "#c0c0c0",
//   "#ffffff",
//   "#ff0000",
//   "#ff8000",
//   "#ffff00",
//   "#80ff00",
//   "#00ff00",
//   "#00ff80",
//   "#00ffff",
//   "#0080ff",
//   "#0000ff",
//   "#8000ff",
//   "#ff00ff",
//   "#ff0080",
//   "#8b4513",
//   "#a0522d",
//   "#cd853f",
// ];

export const Toolbar = ({
  selectedToolId,
  setSelectedToolId,
  selectedColor,
  setSelectedColor,
  onClear,
  onHide,
  style: toolbarStyle,
}: ToolbarProps) => {
  const toolbarRef = useRef<HTMLDivElement | null>(null);
  const clearRef = useRef<HTMLButtonElement | null>(null);
  const toolRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [opacity, setOpacity] = useState(100);

  React.useEffect(() => {
    if (!selectedColor) {
      setSelectedColor("#ff0000");
    }
  }, [selectedColor, setSelectedColor]);

  const getRGBAColor = (color: string, opacity: number) => {
    if (color.startsWith("hsl")) {
      const tempDiv = document.createElement("div");
      tempDiv.style.color = color;
      document.body.appendChild(tempDiv);
      const computedColor = getComputedStyle(tempDiv).color;
      document.body.removeChild(tempDiv);

      const rgbMatch = computedColor.match(/\d+/g);
      if (rgbMatch) {
        return `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, ${opacity / 100})`;
      }
    }

    if (color.startsWith("#")) {
      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${opacity / 100})`;
    }

    if (color.startsWith("rgb(")) {
      const rgbMatch = color.match(/\d+/g);
      if (rgbMatch) {
        return `rgba(${rgbMatch[0]}, ${rgbMatch[1]}, ${rgbMatch[2]}, ${opacity / 100})`;
      }
    }

    return color;
  };

  const handleSelectTool = (toolId: string) => {
    if (toolId === "eraser") {
    } else if (!selectedColor) {
      setSelectedColor("#ff0000");
    }

    setSelectedToolId(toolId);
  };

  const handleSelectColor = (color: string) => {
    if (!selectedToolId || selectedToolId === "eraser") {
      setSelectedToolId("pen");
    }

    setSelectedColor(color);
    setShowColorPicker(false);
  };

  const handleClear = () => {
    setSelectedToolId(null);
    setSelectedColor(null);
    onClear();
  };

  // const handleBrushClick = () => {
  //   handleSelectTool("brush");
  // };

  const fullToolbarStyle = Object.assign(
    {
      boxShadow:
        "0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 16px rgba(0, 0, 0, 0.08)",
      backdropFilter: "blur(20px)",
      border: "1px solid rgba(255, 255, 255, 0.2)",
    },
    toolbarStyle,
  );

  return (
    <>
      {/* Compact Toolbar */}
      <div
        ref={toolbarRef}
        className="inline-block z-10"
        style={{
          ...fullToolbarStyle,
          background: "rgba(20, 20, 20, 0.7)",
          borderRadius: "16px",
          boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
          backdropFilter: "blur(13.6px)",
          WebkitBackdropFilter: "blur(13.6px)",
          border: "1px solid rgba(255, 255, 255, 0.1)",
          padding: window.innerWidth < 768 ? "6px 12px" : "8px 16px",
          display: "flex",
          alignItems: "center",
          gap: window.innerWidth < 768 ? "4px" : "8px",
        }}
      >
        {/* Tool Buttons */}
        {tools.map((tool) => (
          <IconButton
            ref={(el) => {
              toolRefs.current[tool.id] = el;
            }}
            key={tool.id}
            onClick={() => handleSelectTool(tool.id)}
            sx={{
              width: window.innerWidth < 768 ? "45px" : "60px",
              height: window.innerWidth < 768 ? "45px" : "60px",
              borderRadius: "50%",
              padding: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor:
                selectedToolId === tool.id
                  ? "rgba(216, 205, 205, 0.2)"
                  : "rgba(255, 255, 255, 0.05)",
              color: "white",
              border:
                selectedToolId === tool.id
                  ? "2px solid rgba(255, 255, 255, 0.3)"
                  : "2px solid transparent",
              transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
              fontSize: "18px",
              "&:hover": {
                backgroundColor:
                  selectedToolId === tool.id
                    ? "rgba(255, 255, 255, 0.3)"
                    : "rgba(255, 255, 255, 0.1)",
                transform: "scale(1.1)",
              },
              "&:active": {
                transform: "scale(0.95)",
              },
            }}
          >
            <tool.Icon
              width={window.innerWidth < 768 ? 28 : 36}
              height={window.innerWidth < 768 ? 28 : 36}
              style={{
                fill: "black",
                color: "black",
              }}
            />
          </IconButton>
        ))}

        {/* Clear Button */}
        <IconButton
          ref={clearRef}
          onClick={handleClear}
          sx={{
            width: window.innerWidth < 768 ? "45px" : "60px",
            height: window.innerWidth < 768 ? "45px" : "60px",
            borderRadius: "50%",
            padding: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(216, 205, 205, 0.2)",
            color: "white",
            border: "2px solid transparent",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            fontSize: "18px",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              transform: "scale(1.1)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          <BrushIcon
            width={window.innerWidth < 768 ? 28 : 36}
            height={window.innerWidth < 768 ? 28 : 36}
            style={{
              fill: "black",
              color: "black",
            }}
          />
        </IconButton>

        {/* Separator */}
        <div
          style={{
            width: "1px",
            height: "30px",
            backgroundColor: "rgba(255, 255, 255, 0.2)",
            margin: "0 4px",
          }}
        />

        {/* Color Button */}
        <div style={{ position: "relative" }}>
          <div
            style={{
              width: "46px",
              height: "46px",
              borderRadius: "50%",
              background:
                "linear-gradient(45deg, #ff6b6b, #4ecdc4, #45b7d1, #f9ca24, #f0932b)",
              padding: "3px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <button
              onClick={() => setShowColorPicker(!showColorPicker)}
              style={{
                width: window.innerWidth < 768 ? "35px" : "40px",
                height: window.innerWidth < 768 ? "35px" : "40px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: selectedColor || "#ff0000",
                cursor: "pointer",
                transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                outline: "none",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.parentElement?.parentElement?.style.setProperty(
                  "transform",
                  "scale(1.1)",
                );
              }}
              onMouseLeave={(e) => {
                e.currentTarget.parentElement?.parentElement?.style.setProperty(
                  "transform",
                  "scale(1)",
                );
              }}
            ></button>
          </div>
          <div
            style={{
              position: "absolute",
              bottom: "-2px",
              right: "-2px",
              width: "16px",
              height: "16px",
              backgroundColor: "white",
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "12px",
              fontWeight: "bold",
              color: "#333",
              boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
            }}
          >
            +
          </div>
        </div>

        {/* Close / hide compact toolbar */}
        <IconButton
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onHide();
          }}
          aria-label="Hide drawing tools"
          sx={{
            width: window.innerWidth < 768 ? "28px" : "32px",
            height: window.innerWidth < 768 ? "28px" : "32px",
            borderRadius: "50%",
            padding: "0",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.1)",
            color: "white",
            marginLeft: "4px",
            transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              backgroundColor: "rgba(255, 255, 255, 0.2)",
              transform: "scale(1.1)",
            },
            "&:active": {
              transform: "scale(0.95)",
            },
          }}
        >
          <IoClose size={16} />
        </IconButton>
      </div>

      {/* Color Picker Modal */}
      {showColorPicker && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={() => setShowColorPicker(false)}
        >
          <div
            style={{
              background: "rgba(20, 20, 20, 0.7)",
              borderRadius: "16px",
              boxShadow: "0 4px 30px rgba(0, 0, 0, 0.1)",
              backdropFilter: "blur(13.6px)",
              WebkitBackdropFilter: "blur(13.6px)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              padding: "20px",
              minWidth: window.innerWidth < 768 ? "auto" : "280px",
              width: window.innerWidth < 768 ? "calc(100vw - 20px)" : "auto",
              maxWidth: window.innerWidth < 768 ? "calc(100vw - 20px)" : "560px",
              maxHeight: "calc(100vh - 24px)",
              overflowY: "auto",
              transform:
                window.innerWidth < 768
                  ? "translateY(-240px)"
                  : "translateY(-270px)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
                color: "white",
                fontSize: "18px",
                fontWeight: "600",
              }}
            >
              <span>Colors</span>
              <IconButton
                onClick={() => setShowColorPicker(false)}
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  padding: "0",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                    transform: "scale(1.1)",
                  },
                  "&:active": {
                    transform: "scale(0.95)",
                  },
                }}
              >
                <IoClose size={20} />
              </IconButton>
            </div>

            {/* Color Spectrum Palette */}
            <div
              style={{
                marginBottom: "20px",
                padding: window.innerWidth < 768 ? "12px" : "16px",
                backgroundColor: "rgba(255, 255, 255, 0.05)",
                borderRadius: "12px",
              }}
            >
              {/* Grayscale Row */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(10, 1fr)",
                  gap: window.innerWidth < 768 ? "3px" : "4px",
                  marginBottom: "8px",
                }}
              >
                {Array.from({ length: 10 }, (_, i) => {
                  const gray = Math.round((i / 9) * 255);
                  const color = `rgb(${gray}, ${gray}, ${gray})`;
                  return (
                    <button
                      key={`gray-${i}`}
                      onClick={() => handleSelectColor(color)}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        backgroundColor: color,
                        border:
                          selectedColor === color
                            ? "2px solid #ffffff"
                            : "1px solid rgba(255, 255, 255, 0.2)",
                        borderRadius: "4px",
                        cursor: "pointer",
                        outline: "none",
                      }}
                    />
                  );
                })}
              </div>

              {/* Color Spectrum Grid */}
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(12, 1fr)",
                  gap: window.innerWidth < 768 ? "3px" : "4px",
                }}
              >
                {Array.from({ length: 144 }, (_, i) => {
                  const row = Math.floor(i / 12);
                  const col = i % 12;

                  const hue = (col / 12) * 360;
                  const saturation = 100 - (row / 11) * 40;
                  const lightness = 90 - (row / 11) * 40;

                  const color = `hsl(${hue}, ${saturation}%, ${lightness}%)`;

                  return (
                    <button
                      key={`spectrum-${i}`}
                      onClick={() => handleSelectColor(color)}
                      style={{
                        width: "100%",
                        aspectRatio: "1 / 1",
                        backgroundColor: color,
                        border:
                          selectedColor === color
                            ? "2px solid #ffffff"
                            : "none",
                        borderRadius: "3px",
                        cursor: "pointer",
                        outline: "none",
                        boxShadow:
                          selectedColor === color
                            ? "0 0 0 1px rgba(255, 255, 255, 0.5)"
                            : "none",
                      }}
                    />
                  );
                })}
              </div>
            </div>

            {/* Opacity Slider */}
            <div style={{ marginBottom: "20px" }}>
              <div
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "14px",
                  fontWeight: "600",
                  marginBottom: "12px",
                  textTransform: "uppercase",
                  letterSpacing: "1px",
                }}
              >
                OPACITY
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "0 4px",
                }}
              >
                <div
                  style={{
                    flex: 1,
                    height: "32px",
                    background: `linear-gradient(to right, transparent 0%, ${selectedColor || "#ff0000"} 100%)`,
                    borderRadius: "16px",
                    position: "relative",
                    border: "1px solid rgba(255, 255, 255, 0.2)",
                    cursor: "pointer",
                  }}
                  onClick={(e) => {
                    const rect = e.currentTarget.getBoundingClientRect();
                    const x = e.clientX - rect.left;
                    const percentage = Math.max(
                      0,
                      Math.min(100, (x / rect.width) * 100),
                    );
                    setOpacity(Math.round(percentage));
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      left: `${opacity}%`,
                      top: "50%",
                      transform: "translate(-50%, -50%)",
                      width: "24px",
                      height: "24px",
                      backgroundColor: selectedColor || "#ff0000",
                      borderRadius: "50%",
                      border: "2px solid white",
                      cursor: "grab",
                      boxShadow: "0 2px 8px rgba(0, 0, 0, 0.3)",
                    }}
                  />
                </div>
                <span
                  style={{
                    color: "white",
                    fontSize: "16px",
                    fontWeight: "600",
                    minWidth: "50px",
                  }}
                >
                  {opacity}%
                </span>
              </div>
            </div>

            {/* Selected Color Display */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <div
                style={{
                  width: "48px",
                  height: "48px",
                  backgroundColor: getRGBAColor(
                    selectedColor || "#ff0000",
                    opacity,
                  ),
                  borderRadius: "8px",
                  border: "2px solid rgba(255, 255, 255, 0.2)",
                  boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
                }}
              />
              <IconButton
                onClick={() => {
                  const colorWithOpacity = getRGBAColor(
                    selectedColor || "#ff0000",
                    opacity,
                  );
                  setSelectedColor(colorWithOpacity);
                }}
                sx={{
                  width: "32px",
                  height: "32px",
                  borderRadius: "50%",
                  backgroundColor: "rgba(255, 255, 255, 0.1)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(255, 255, 255, 0.2)",
                  },
                }}
              >
                <span style={{ fontSize: "20px", fontWeight: "bold" }}>+</span>
              </IconButton>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
